import React from 'react';
import {
  ConfirmationTypes,
  deleteWorkshift,
  IPharmacyEventsData,
  PharmacyLegendVariant,
  RecurrenceActions,
  TypeOfUser,
} from '@pharmaplan/common';
import { useAppDispatch } from '../useAppDispatch';
import { resetDialog, setDialog } from '../../reducers/dialogReducer';
import UpdateModal from '../../components/Modals/UpdateModal';
import useDrawerNavigation from '../useDrawerNavigation';
import ConfirmationModal from '../../components/Modals/ConfirmationModal';
import { renderScreen } from '../../actions/drawerActions';
import { ScreenTypes } from '../../helpers/Constants';
import ContractDeleteMessage from '../../components/SelfService/WorkshiftSummary/WorkshiftSummaryList/SummaryContent/ContractDeleteMessage';
import useSelfService from '../useSelfService';

interface IUseSummaryContent {
  event: IPharmacyEventsData;
  type: PharmacyLegendVariant;
  preventInteraction?: boolean;
  longTermShiftContractId?: string;
}

const useSummaryContent = ({
  event,
  type,
  preventInteraction,
  longTermShiftContractId,
}: IUseSummaryContent) => {
  const dispatch = useAppDispatch();
  const { openDrawer } = useDrawerNavigation();
  const { isSelfService } = useSelfService();

  const {
    bookingId,
    totalShiftCount = 0,
    workshiftId,
    recurrenceId,
    startDate,
    professionalId,
    pharmacistId,
    bookedShiftCount = 0,
  } = event ?? {};

  const goToUpdate = () => {
    if (recurrenceId) {
      dispatch(
        setDialog({
          showCloseButton: false,
          Component: (
            <UpdateModal
              type={TypeOfUser.pharmacy}
              handleSelect={(selected: RecurrenceActions) => {
                openDrawer({
                  eventIds: [workshiftId],
                  recurrenceType: selected,
                  recurrenceId,
                  date: startDate,
                  type,
                  openDetails: true,
                });
              }}
            />
          ),
          heading: {
            title: '',
          },
        }),
      );
      return;
    }

    dispatch(
      renderScreen({
        screenNumber: 2,
        screenType: ScreenTypes.updateWorkshift,
        eventIds: [workshiftId],
        date: startDate,
        type,
      }),
    );
  };

  const handleDelete = () => {
    dispatch(
      deleteWorkshift({
        id: workshiftId,
        recurrenceId: '',
      }),
    );

    dispatch(resetDialog());
  };

  const confirmRejection = () => {
    dispatch(
      setDialog({
        heading: {
          title: '',
        },
        Component: (
          <ConfirmationModal
            type={ConfirmationTypes.deleteWorkshift}
            confirmAction={handleDelete}
          />
        ),
        showCloseButton: false,
      }),
    );
  };

  const confirmMultipleDelete = () => {
    dispatch(
      setDialog({
        heading: {
          title: '',
        },
        Component: (
          <ConfirmationModal
            type={ConfirmationTypes.deleteMultipleContracts}
            customMessage={(
              <ContractDeleteMessage
                totalShiftCount={totalShiftCount - bookedShiftCount ?? 0}
              />
            )}
            confirmAction={handleDelete}
          />
        ),
        showCloseButton: false,
      }),
    );
  };

  const openBookedPharmacy = () => {
    dispatch(
      renderScreen({
        screenNumber: 3,
        screenType: ScreenTypes.workshiftSummary,
        eventIds: [bookingId ?? ''],
        eventId: bookingId,
        date: startDate,
        pharmacistId: isSelfService ? professionalId : pharmacistId,
        extraParameters: { longTermShiftContractId },
        workshiftId,
        type,
      }),
    );
  };

  const goToRequestedList = () => {
    dispatch(
      renderScreen({
        eventIds: [workshiftId ?? ''],
        eventId: workshiftId,
        type,
        screenNumber: 2,
        date: startDate,
        screenType: ScreenTypes.workshiftSummary,
        extraParameters: {
          preventInteraction,
          longTermShiftContractId,
        },
      }),
    );
  };

  return {
    openBookedPharmacy,
    goToRequestedList,
    goToUpdate,
    confirmMultipleDelete,
    confirmRejection,
  };
};

export default useSummaryContent;
